import React, { useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { useDebouncedCallback } from 'use-debounce';
import css from './BorderColorInput.module.scss';

export const SYSTEM_DEFAULT_BORDER_COLOR = '#FF5733';

const HEX_RE = /^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/;

type BorderColorInputProps = {
  value: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
  testIdPrefix?: string;
  placeholder?: string;
};

function BorderColorInputUnmemoized({
  value, onChange, disabled, testIdPrefix = 'border-color', placeholder = '#hex',
}: BorderColorInputProps) {
  const hexValue = value ?? '';
  const swatchValue = value && HEX_RE.test(value) ? value : SYSTEM_DEFAULT_BORDER_COLOR;
  const trimmed = hexValue.trim();
  const isInvalid = trimmed.length > 0 && !HEX_RE.test(trimmed);

  const debouncedCommit = useDebouncedCallback(onChange, 120);

  const handleSwatchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedCommit(e.target.value);
  }, [debouncedCommit]);

  const handleHexChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    onChange(next === '' ? null : next);
  }, [onChange]);

  return (
    <div className={css.colorGroup}>
      <Form.Control
        type="color"
        size="sm"
        value={swatchValue}
        onChange={handleSwatchChange}
        disabled={disabled}
        className={css.colorSwatch}
        data-testid={`${testIdPrefix}-swatch`}
      />
      <Form.Control
        size="sm"
        value={hexValue}
        onChange={handleHexChange}
        disabled={disabled}
        placeholder={placeholder}
        className={css.colorInput}
        isInvalid={isInvalid}
        aria-invalid={isInvalid || undefined}
        data-testid={`${testIdPrefix}-input`}
      />
    </div>
  );
}

export const BorderColorInput = React.memo(BorderColorInputUnmemoized);
